import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Label } from "types/label";
import moment from "moment";

export enum CheckingStatus {
  Checking = "Checking",
  Available = "Available",
  NotAvailable = "NotAvailable",
}

export const useLabels = (
  currentPage: number = 0,
  pageCount: number = 100,
) => {
  const [labels, setLabels] = useState<Label[]>([]);
  const [fetching, setFetching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<CheckingStatus>(CheckingStatus.Checking);
  const [total, setTotal] = useState(0);
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);

  const getLabels = useCallback(async (page: number = currentPage, perPage: number = pageCount) => {
    try {
      setFetching(true);
      const query = new URLSearchParams({ page: String(page), per_page: String(perPage) }).toString();

      const { data } = await axios.get(
        `${API_BASE_URL}${API_URLS.Labels}?${query}`,
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
      );

      setLabels(data.data);
      setTotal(data.meta.total);
    } catch (e) {
      console.error("Error fetching labels:", e);
    } finally {
      setFetching(false);
    }
  }, [token, currentPage, pageCount]);

  const createLabel = useCallback(async (name: string, countryId: string) => {
    try {
      setCreating(true);
      await axios.post(
        `${API_BASE_URL}${API_URLS.Labels}`,
        { name, country_id: countryId ? countryId : 3, year: moment().format("YYYY") },
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
      );
      await getLabels(); 
    } catch (e) {
      console.error("Error creating label:", e);
      throw e;
    } finally {
      setCreating(false);
    }
  }, [token, getLabels]);
  

  const deleteLabel = useCallback(async (id: string) => {
    try {
      setDeleting(true);
      await axios.delete(
        `${API_BASE_URL}${API_URLS.Labels}/${id}`,
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
      );
      await getLabels();
    } catch (e) {
      console.error("Error deleting label:", e);
      throw e;
    } finally {
      setDeleting(false);
    }
  }, [token, getLabels]);

  const checkLabel = useCallback(async (name: string) => {
    try {
      setChecking(true);
      setCheckingStatus(CheckingStatus.Checking);
      const { data } = await axios.post(
        `${API_BASE_URL}${API_URLS.CheckLabelAvailability}`,
        { name },
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
      );

      setCheckingStatus(data.data.available ? CheckingStatus.Available : CheckingStatus.NotAvailable);
    } catch (e) {
      console.error("Error checking label:", e);
      throw e;
    } finally {
      setChecking(false);
    }
  }, [token]);

  const resetCheckLabel = useCallback(() => {
    setCheckingStatus(CheckingStatus.Checking);
    setChecking(false);
  }, []);

  useEffect(() => {
    getLabels();
  }, [getLabels]);

  return {
    labels,
    total,
    fetching,
    creating,
    checking,
    deleting,
    checkingStatus,
    getLabels,
    createLabel,
    deleteLabel,
    checkLabel,
    resetCheckLabel,
  };
};
